'use client';

import type { RegisterOptions } from 'react-hook-form';

import { clsx } from 'clsx';
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';

import { IconX, IconClock, IconChevronDown } from '@tabler/icons-react';

import { useSettingsStore } from '@/store/useSettingsStore';

interface ITimeInput {
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register?: any;
  registerOptions?: RegisterOptions;
  error?: string | string[];
  name: string;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  clearable?: boolean;
  className?: string;
  inputWrapperClassName?: string;
  required?: boolean;
  dataTestId?: string;
  is24Hour?: boolean;
  minuteStep?: number;
}

interface ITimeInputLabel {
  label?: string;
  name: string;
  required?: boolean;
}

interface ITimeInputError {
  error?: string | string[];
  name: string;
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) =>
  String(i + 1).padStart(2, '0')
);
const HOURS_24 = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0')
);

const generateMinutes = (step: number) =>
  Array.from({ length: Math.floor(60 / step) }, (_, i) =>
    String(i * step).padStart(2, '0')
  );

const PERIODS = ['AM', 'PM'] as const;

const TIME_OPTION_BASE_CLASS =
  'w-full py-1.5 text-sm text-center cursor-pointer rounded-md mx-auto hover:bg-neutral-50 transition-colors';
const TIME_OPTION_SELECTED_CLASS =
  'bg-brand-light-50 text-brand-default font-semibold';
const TIME_OPTION_UNSELECTED_CLASS = 'text-neutral-900 font-medium';

const TimeInputLabel = ({ label, name, required }: ITimeInputLabel) => {
  if (!label) return null;
  return (
    <label
      htmlFor={name}
      className="block text-sm font-medium text-neutral-primary leading-5"
    >
      {label} {required && <span className="text-red-500">*</span>}
    </label>
  );
};

const TimeInputError = ({ error, name }: ITimeInputError) => {
  if (!error) return null;
  const errors = Array.isArray(error) ? error : [error];
  return (
    <>
      {errors.map((e, i) => (
        <p
          key={i}
          id={`${name}-error`}
          className="text-sm font-normal text-error-default"
          role="alert"
        >
          {e}
        </p>
      ))}
    </>
  );
};

/**
 * Parse a time string (HH:mm) into hour, minute, period for 12h display,
 * or hour, minute for 24h display.
 */
const parseTimeValue = (
  timeValue: string | undefined,
  is24Hour: boolean
): { hour: string; minute: string; period: 'AM' | 'PM' } => {
  if (!timeValue) {
    return { hour: '', minute: '', period: 'AM' };
  }

  const parts = timeValue.split(':');
  let h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);

  if (isNaN(h) || isNaN(m)) {
    return { hour: '', minute: '', period: 'AM' };
  }

  if (is24Hour) {
    return {
      hour: String(h).padStart(2, '0'),
      minute: String(m).padStart(2, '0'),
      period: 'AM',
    };
  }

  const period: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;

  return {
    hour: String(h).padStart(2, '0'),
    minute: String(m).padStart(2, '0'),
    period,
  };
};

/**
 * Build a HH:mm (24h) string from hour, minute, period.
 */
const buildTimeValue = (
  hour: string,
  minute: string,
  period: 'AM' | 'PM',
  is24Hour: boolean
): string => {
  if (!hour || !minute) return '';

  let h = parseInt(hour, 10);

  if (!is24Hour) {
    if (period === 'AM' && h === 12) h = 0;
    else if (period === 'PM' && h !== 12) h += 12;
  }

  return `${String(h).padStart(2, '0')}:${minute}`;
};

interface ITimeInputTrigger {
  name: string;
  isOpen: boolean;
  disabled?: boolean;
  error?: string | string[];
  placeholder: string;
  label?: string;
  displayValue: string;
  dataTestId: string;
  required: boolean;
  inputWrapperClassName?: string;
  stateClasses: string;
  handleToggle: (e: React.MouseEvent) => void;
  onClear?: (e: React.MouseEvent) => void;
}

const TimeInputTrigger = React.forwardRef<HTMLDivElement, ITimeInputTrigger>(
  (
    {
      name,
      isOpen,
      disabled,
      error,
      placeholder,
      label,
      displayValue,
      dataTestId,
      required,
      inputWrapperClassName,
      stateClasses,
      handleToggle,
      onClear,
    },
    ref
  ) => (
    <div className="relative" ref={ref}>
      <button
        type="button"
        id={name}
        className={clsx(
          'w-full py-2.5 bg-white border text-sm border-neutral-light-200 rounded-lg flex justify-between gap-2 items-center cursor-pointer px-4 focus-within:outline-2 focus-within:outline-offset-2 hover:border-brand-default focus-within:border-[#e3ecff] focus-within:outline-[#e3ecff]',
          disabled &&
            'border-neutral-200! bg-neutral-50! cursor-not-allowed! text-neutral-disabled focus:outline-none',
          error && 'border-error-default',
          inputWrapperClassName,
          stateClasses
        )}
        onClick={handleToggle}
        disabled={disabled}
        data-testid={dataTestId}
        aria-label={label ? undefined : placeholder || name}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${name}-error` : undefined}
        aria-required={required}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <IconClock
            size={20}
            stroke={1.5}
            className="text-neutral-400 shrink-0"
          />
          {displayValue ? (
            <span className="truncate text-sm text-neutral-primary font-medium">
              {displayValue}
            </span>
          ) : (
            <span className="text-neutral-secondary font-medium text-sm">
              {placeholder}
            </span>
          )}
        </div>
        {displayValue && onClear ? (
          <span
            role="button"
            aria-label="Clear time"
            onClick={(e) => {
              e.stopPropagation();
              onClear(e);
            }}
            className="text-neutral-secondary hover:text-neutral-primary transition-colors shrink-0 cursor-pointer"
          >
            <IconX size={16} />
          </span>
        ) : (
          <IconChevronDown
            size={18}
            className={clsx(
              'transition-transform duration-300 text-neutral-disabled',
              isOpen && 'rotate-180'
            )}
          />
        )}
      </button>
    </div>
  )
);
TimeInputTrigger.displayName = 'TimeInputTrigger';

interface ITimeInputDropdown {
  isOpen: boolean;
  openUpwards: boolean;
  dropdownPosition: { top: number; left: number; width: number };
  direction: string;
  is24Hour: boolean;
  hours: string[];
  minutes: string[];
  hour: string;
  minute: string;
  period: 'AM' | 'PM';
  hourListRef: React.RefObject<HTMLDivElement | null>;
  minuteListRef: React.RefObject<HTMLDivElement | null>;
  periodListRef: React.RefObject<HTMLDivElement | null>;
  selectHour: (h: string) => void;
  selectMinute: (m: string) => void;
  selectPeriod: (p: 'AM' | 'PM') => void;
}

const TimeInputDropdown = React.forwardRef<HTMLDivElement, ITimeInputDropdown>(
  (
    {
      isOpen,
      openUpwards,
      dropdownPosition,
      direction,
      is24Hour,
      hours,
      minutes,
      hour,
      minute,
      period,
      hourListRef,
      minuteListRef,
      periodListRef,
      selectHour,
      selectMinute,
      selectPeriod,
    },
    ref
  ) => {
    if (!isOpen) return null;

    return createPortal(
      <div
        ref={ref}
        className="fixed z-9999 bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden"
        style={{
          top: openUpwards ? 'auto' : dropdownPosition.top,
          bottom: openUpwards
            ? window.innerHeight - dropdownPosition.top
            : 'auto',
          left: direction === 'rtl' ? undefined : dropdownPosition.left,
          right:
            direction === 'rtl'
              ? window.innerWidth -
                dropdownPosition.left -
                dropdownPosition.width
              : undefined,
          width: dropdownPosition.width,
          minWidth: '14rem',
        }}
        data-testid="time-dropdown"
      >
        <div
          className={clsx(
            'flex border-b border-neutral-100',
            is24Hour ? 'grid grid-cols-2' : 'grid grid-cols-3'
          )}
        >
          <div className="text-xs font-semibold text-neutral-500 text-center py-2 uppercase tracking-wide">
            Hr
          </div>
          <div className="text-xs font-semibold text-neutral-500 text-center py-2 uppercase tracking-wide">
            Min
          </div>
          {!is24Hour && (
            <div className="text-xs font-semibold text-neutral-500 text-center py-2 uppercase tracking-wide">
              &nbsp;
            </div>
          )}
        </div>

        <div
          className={clsx(
            'flex',
            is24Hour ? 'grid grid-cols-2' : 'grid grid-cols-3'
          )}
        >
          <div
            ref={hourListRef}
            className="overflow-y-auto max-h-52 border-r border-neutral-100 py-1 scrollbar-thin"
          >
            {hours.map((h) => (
              <button
                key={h}
                type="button"
                data-value={h}
                className={clsx(
                  TIME_OPTION_BASE_CLASS,
                  hour === h
                    ? TIME_OPTION_SELECTED_CLASS
                    : TIME_OPTION_UNSELECTED_CLASS
                )}
                onClick={() => selectHour(h)}
              >
                {h}
              </button>
            ))}
          </div>

          <div
            ref={minuteListRef}
            className={clsx(
              'overflow-y-auto max-h-52 py-1 scrollbar-thin',
              !is24Hour && 'border-r border-neutral-100'
            )}
          >
            {minutes.map((m) => (
              <button
                key={m}
                type="button"
                data-value={m}
                className={clsx(
                  TIME_OPTION_BASE_CLASS,
                  minute === m
                    ? TIME_OPTION_SELECTED_CLASS
                    : TIME_OPTION_UNSELECTED_CLASS
                )}
                onClick={() => selectMinute(m)}
              >
                {m}
              </button>
            ))}
          </div>

          {!is24Hour && (
            <div ref={periodListRef} className="py-1 flex flex-col">
              {PERIODS.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-value={p}
                  className={clsx(
                    TIME_OPTION_BASE_CLASS,
                    period === p
                      ? TIME_OPTION_SELECTED_CLASS
                      : TIME_OPTION_UNSELECTED_CLASS
                  )}
                  onClick={() => selectPeriod(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }
);
TimeInputDropdown.displayName = 'TimeInputDropdown';

const TimeInput = ({
  disabled,
  error,
  register,
  registerOptions,
  name,
  placeholder = 'HH:MM',
  value,
  onChange,
  clearable = false,
  label,
  className,
  inputWrapperClassName,
  required = false,
  dataTestId = 'time-input',
  is24Hour = false,
  minuteStep = 1,
}: ITimeInput) => {
  const direction = useSettingsStore((state) => state.settings.direction);

  const [isOpen, setIsOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);
  const periodListRef = useRef<HTMLDivElement>(null);

  const registered = register?.(name, {
    ...(registerOptions || {}),
  });

  const [internalValue, setInternalValue] = useState<string | undefined>(value);

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const currentValue = registered?.value || internalValue;
  const { hour, minute, period } = parseTimeValue(currentValue, is24Hour);
  const hours = is24Hour ? HOURS_24 : HOURS_12;
  const minutes = generateMinutes(minuteStep);

  useEffect(() => {
    if (!isOpen) return;

    const scrollToSelected = (
      containerRef: React.RefObject<HTMLDivElement | null>,
      selectedValue: string
    ) => {
      if (!containerRef.current || !selectedValue) return;
      const selectedEl = containerRef.current.querySelector(
        `[data-value="${selectedValue}"]`
      ) as HTMLElement | null;
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    };

    requestAnimationFrame(() => {
      scrollToSelected(hourListRef, hour);
      scrollToSelected(minuteListRef, minute);
      if (!is24Hour) {
        scrollToSelected(periodListRef, period);
      }
    });
  }, [isOpen, hour, minute, period, is24Hour]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !buttonRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = (event: Event) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(target) &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = document.documentElement.clientHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUpwards = spaceBelow < 280 && spaceAbove > spaceBelow;

      setOpenUpwards(shouldOpenUpwards);
      setDropdownPosition({
        top: shouldOpenUpwards ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const triggerChange = useCallback(
    (newValue: string) => {
      setInternalValue(newValue);
      onChange?.(newValue);
      if (!registered) return;
      const nativeValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;
      const hiddenInput = document.getElementById(
        `${name}-hidden`
      ) as HTMLInputElement | null;
      if (hiddenInput && nativeValueSetter) {
        nativeValueSetter.call(hiddenInput, newValue);
        hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    [registered, name, onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      triggerChange('');
    },
    [triggerChange]
  );

  const selectHour = (h: string) =>
    triggerChange(buildTimeValue(h, minute || '00', period || 'AM', is24Hour));

  const selectMinute = (m: string) =>
    triggerChange(
      buildTimeValue(
        hour || (is24Hour ? '00' : '12'),
        m,
        period || 'AM',
        is24Hour
      )
    );

  const selectPeriod = (p: 'AM' | 'PM') =>
    triggerChange(buildTimeValue(hour || '12', minute || '00', p, is24Hour));

  const displayValue = (() => {
    if (!hour && !minute) return '';
    const display = `${hour}:${minute}`;
    return !is24Hour && period ? `${display} ${period}` : display;
  })();

  const stateClasses = clsx({
    'border-neutral-light-200 bg-neutral-100 focus-within:outline-none':
      disabled,
    'border-error-default focus-within:outline-none bg-white':
      !disabled && error,
  });

  return (
    <div className={clsx('w-full flex flex-col gap-1.5', className)}>
      <TimeInputLabel label={label} name={name} required={required} />

      <input
        type="hidden"
        id={`${name}-hidden`}
        {...registered}
        value={currentValue || ''}
      />

      <TimeInputTrigger
        ref={buttonRef}
        name={name}
        isOpen={isOpen}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
        label={label}
        displayValue={displayValue}
        dataTestId={dataTestId}
        required={required}
        inputWrapperClassName={inputWrapperClassName}
        stateClasses={stateClasses}
        handleToggle={handleToggle}
        onClear={clearable ? handleClear : undefined}
      />

      <TimeInputDropdown
        ref={dropdownRef}
        isOpen={isOpen}
        openUpwards={openUpwards}
        dropdownPosition={dropdownPosition}
        direction={direction}
        is24Hour={is24Hour}
        hours={hours}
        minutes={minutes}
        hour={hour}
        minute={minute}
        period={period}
        hourListRef={hourListRef}
        minuteListRef={minuteListRef}
        periodListRef={periodListRef}
        selectHour={selectHour}
        selectMinute={selectMinute}
        selectPeriod={selectPeriod}
      />

      <TimeInputError error={error} name={name} />
    </div>
  );
};

export default TimeInput;
